/**
 * Centralized type exports
 */
import { SimilarCar } from './vehicle';
export * from './vehicle';
export * from './api';


// 1. Define the possible statuses for a single analysis step
export type AnalysisStatus = 'pending' | 'in_progress' | 'completed' | 'error' | 'skipped';

// 2. Define the structure for a single analysis step, our new "source of truth"
export interface AnalysisStep {
  key: string;
  name: string;
  description: string;
  status: AnalysisStatus;
  data?: any; // Holds the successful result data
  error?: string; // Holds the error message if it failed
}

// 3. Define the overall state shape for all steps (a dictionary of AnalysisStep objects)
export type AnalysisStepsState = Record<string, Omit<AnalysisStep, 'key' | 'name' | 'description'>>;


export const analysisSteps: Omit<AnalysisStep, 'status' | 'data' | 'error'>[] = [
  { key: 'flags_and_highlights', name: 'Flags & Highlights', description: 'Analyzing key vehicle details and history.' },
  { key: 'common_issues', name: 'Common Issues', description: 'Identifying potential common problems.' },
  { key: 'maintenance', name: 'Maintenance', description: 'Recommending maintenance tasks.' },
  { key: 'cpo_eligibility', name: 'CPO Eligibility', description: 'Checking for Certified Pre-Owned eligibility.' },
  { key: 'image_analysis', name: 'Image Analysis', description: 'Analyzing vehicle images for issues.' },
  { key: 'suggested_repairs', name: 'Detected Issues', description: 'Suggesting potential repairs.' },
  { key: 'market_insights', name: 'Market Insights', description: 'Providing market analysis and insights.' },
];

export interface AnalysisResult {
  [key: string]: any;
}

// 4. Update the main context state to use the new unified steps state
export interface PpiAnalysisState {
  analysisStarted: boolean;
  loading: boolean;
  error: string | null;
  analysisSteps: AnalysisStepsState;
  isComplete: boolean;
  sentEmailAddress: string | null;
  emailError: string | null;
  similarCars: SimilarCar[];
  startAnalysis: (sendEmail: boolean) => void;
  resetState: () => void;
}

// 5. Define the initial state based on the new structure
const initialAnalysisSteps = (): AnalysisStepsState => {
  const steps: AnalysisStepsState = {};
  analysisSteps.forEach(step => {
    steps[step.key] = { status: 'pending' };
  });
  return steps;
};

export const initialPpiAnalysisState: PpiAnalysisState = {
  analysisStarted: false,
  loading: false,
  error: null,
  analysisSteps: initialAnalysisSteps(),
  isComplete: false,
  sentEmailAddress: null,
  emailError: null,
  similarCars: [],
  startAnalysis: () => {},
  resetState: () => {},
};
